// pages/ManageManagers.tsx — FINAL RTP FULLY INTEGRATED VERSION
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import DOMPurify from 'dompurify';
import { Loader2 } from 'lucide-react';
import useAdmin from '../hooks/useAdmin';
import { useConfirmModal } from '../utils/modal';
import ConfirmModal from '../../shared/components/ConfirmModal';
import { toast } from 'react-hot-toast';

interface Manager {
  id?: string;
  login_id?: string;
  email?: string;
  name: string;
  phone?: string;
  credits: number;
  cashout_rate?: number;
  is_verified: boolean;
  suspended: boolean;
}

const toNumber = (value: unknown, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

interface SearchForm {
  search: string;
}

export default function ManageManagers() {
  const [managers, setManagers] = useState<Manager[]>([]);
  const [loading, setLoading] = useState(false);
  const { token, fetchManagers, suspendManager, deleteManager, error, setError, socket } = useAdmin();
  const { isOpen, message, showConfirmModal, handleConfirm, handleCancel } = useConfirmModal();
  const { register, watch } = useForm<SearchForm>({ defaultValues: { search: '' } });
  const search = watch('search', '');

  useEffect(() => {
    if (token) {
      setLoading(true);
      fetchManagers()
        .then((data: Manager[]) => {
          setManagers((data || []) as any[]);
          setLoading(false);
        })
        .catch((err: Error) => {
          setError(`Failed to fetch managers: ${err.message}`);
          setLoading(false);
        });
    }
  }, [token, fetchManagers, setError]);

  useEffect(() => {
    if (!socket) return;

    socket.on('manager-updated', () => {
      toast('Manager list updated in real-time');
      fetchManagers().then(data => setManagers((data || []) as any[]));
    });

    // FIX: both email and managerId are optional in socket event data
    socket.on('manager-suspended', (data: { email?: string; managerId?: string; suspended: boolean }) => {
      toast.success(`Manager ${data.email || data.managerId || ''} ${data.suspended ? 'suspended' : 'unsuspended'}`);
      setManagers(prev =>
        prev.map(m =>
          (data.managerId && m.id === data.managerId) || (data.email && m.email === data.email)
            ? { ...m, suspended: data.suspended }
            : m
        )
      );
    });

    // FIX: both email and managerId are optional in socket event data
    socket.on('manager-deleted', (data: { email?: string; managerId?: string }) => {
      toast.success('Manager deleted successfully');
      setManagers(prev =>
        prev.filter(m =>
          !(data.managerId && m.id === data.managerId) &&
          !(data.email && m.email === data.email)
        )
      );
    });

    socket.on('credits-updated', (data: { managerId?: string; credits: number }) => {
      if (data.managerId) {
        toast.success(`Manager credits updated to ${data.credits}`);
        setManagers(prev =>
          prev.map(m => m.id === data.managerId ? { ...m, credits: data.credits } : m)
        );
      }
    });

    return () => {
      socket.off('manager-updated');
      socket.off('manager-suspended');
      socket.off('manager-deleted');
      socket.off('credits-updated');
    };
  }, [socket, fetchManagers]);

  const filtered = managers.filter(m =>
    (m.name?.toLowerCase().includes(search.toLowerCase()) || false) ||
    ((m.login_id || m.email || '').toLowerCase().includes(search.toLowerCase()) || false)
  );

  const handleSuspend = (manager: Manager) => {
    const action = manager.suspended ? 'unsuspend' : 'suspend';
    showConfirmModal(
      `Are you sure you want to ${action} manager ${manager.name}? ${action === 'suspend' ? 'This will revoke RTP control access.' : 'This will restore RTP control access.'}`,
      async () => {
        try {
          setManagers(prev =>
            prev.map(m => m.id === manager.id ? { ...m, suspended: !manager.suspended } : m)
          );
          await suspendManager(manager.login_id || manager.email || '', !manager.suspended);
          toast.success(`Manager ${manager.login_id || manager.name} ${action}ed`);
        } catch (err: any) {
          setManagers(prev =>
            prev.map(m => m.id === manager.id ? { ...m, suspended: manager.suspended } : m)
          );
          setError(`Failed to ${action}: ${err.message}`);
          toast.error(`Failed to ${action}: ${err.message}`);
        }
      }
    );
  };

  const handleDelete = (manager: Manager) => {
    showConfirmModal(
      `Delete manager ${manager.name}? This will permanently revoke RTP control access and cannot be undone.`,
      async () => {
        try {
          setManagers(prev => prev.filter(m => m.id !== manager.id));
          await deleteManager(manager.login_id || manager.email || '');
          toast.success(`Manager deleted: ${manager.login_id || manager.name}`);
        } catch (err: any) {
          setManagers(prev => [...prev, manager]);
          setError(`Delete failed: ${err.message}`);
          toast.error(`Delete failed: ${err.message}`);
        }
      }
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h2 className="text-3xl font-bold mb-6 text-gray-900">Manage Managers</h2>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-lg flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-700 hover:text-red-900 font-bold">✕</button>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <input
            type="text"
            placeholder="Search by name or login ID..."
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            {...register('search')}
            aria-label="Search managers"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-indigo-600" size={48} />
            <span className="ml-4 text-xl text-gray-700">Loading managers...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-gray-500 py-20">
            No managers found {search ? `matching "${search}"` : ''}.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-gray-50">
                <tr>
                  <th className="p-4 text-left font-semibold text-gray-700">Name</th>
                  <th className="p-4 text-left font-semibold text-gray-700">Login ID</th>
                  <th className="p-4 text-left font-semibold text-gray-700">Credits</th>
                  <th className="p-4 text-left font-semibold text-gray-700">Cashout Rate</th>
                  <th className="p-4 text-left font-semibold text-gray-700">Verified</th>
                  <th className="p-4 text-left font-semibold text-gray-700">Status</th>
                  <th className="p-4 text-left font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(m => (
                  <tr key={m.id || m.login_id || m.name} className="border-b hover:bg-gray-50 transition">
                    <td className="p-4 font-medium" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(m.name || 'N/A') }} />
                    <td className="p-4 font-mono font-bold text-indigo-600">{m.login_id || '—'}</td>
                    <td className="p-4 font-medium text-indigo-700">${toNumber(m.credits).toFixed(2)}</td>
                    <td className="p-4 text-gray-600">
                      {m.cashout_rate !== undefined && m.cashout_rate !== null
                        ? `${toNumber(m.cashout_rate).toFixed(2)}%`
                        : 'N/A'}
                    </td>
                    <td className="p-4">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                        m.is_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {m.is_verified ? 'Verified' : 'Pending'}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                        m.suspended ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {m.suspended ? 'Suspended' : 'Active'}
                      </span>
                    </td>
                    <td className="p-4 space-x-3">
                      <button
                        className={`px-4 py-2 text-white text-sm rounded-lg transition shadow-sm ${
                          m.suspended ? 'bg-green-600 hover:bg-green-700' : 'bg-yellow-600 hover:bg-yellow-700'
                        }`}
                        onClick={() => handleSuspend(m)}
                        aria-label={`${m.suspended ? 'Unsuspend' : 'Suspend'} manager ${m.login_id || m.name}`}
                      >
                        {m.suspended ? 'Unsuspend' : 'Suspend'}
                      </button>
                      <button
                        className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition shadow-sm"
                        onClick={() => handleDelete(m)}
                        aria-label={`Delete manager ${m.login_id || m.name}`}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={isOpen}
        message={message}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
}
